import { createGlobalStyle } from 'styled-components';
import type { Theme } from '../../theme';
import { fade } from '../../theme/animations';

const FONT_SIZE_LINE_HEIGHTS: { [key: string]: number } = {
    '14': 18,
    '15': 19,
    '16': 20,
    '17': 21,
    '18': 22,
    '19': 23,
};

const Global = createGlobalStyle<{ theme: Theme; textMessageSize: number }>`
    * {
        outline: none;
        font-family: inherit;
    }

    html {
        -ms-overflow-style: -ms-autohiding-scrollbar;
    }

    body {
        font-family: ${props => props.theme.fontFamily};
        color: ${props => props.theme.root.body.color};
        font-variant-ligatures: common-ligatures;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        background: ${props => props.theme.root.body.background};
        transition: background .2s;
        padding: 0;
        margin: 0;
    }

    #root, body, html {
        height: 100%;
        width: 100%;
        min-height: 100vh;
        overflow: hidden;
    }

    [hidden] {
        display: none;
    }

    [data-navigation-item]:focus {
        background-color: ${props => props.theme.root.colors.basicColors['4%']};
    }

    [data-message-title] {
        font-size: ${props => props.textMessageSize + 1}px;
        line-height: ${props => FONT_SIZE_LINE_HEIGHTS[String(props.textMessageSize)] + 1}px;
    }

    [data-message-text] {
        font-size: ${props => props.textMessageSize}px;
        line-height: ${props => FONT_SIZE_LINE_HEIGHTS[String(props.textMessageSize)]}px;
    }

    [data-message-line-height] {
        line-height: ${props => FONT_SIZE_LINE_HEIGHTS[String(props.textMessageSize)]}px;
    }

    [data-message-meta] {
        height: ${props => FONT_SIZE_LINE_HEIGHTS[String(props.textMessageSize)]}px;
    }

    [data-reach-menu] {
        position: absolute;
        display: block;
        z-index: 10;
        animation: ${fade} 0.3s ease;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    [data-reach-popover] {
        z-index: 10;
    }

    [data-reach-popover]:has(.top-layer[data-reach-menu-list]) {
        z-index: 9999;
    }

    [data-reach-popover], [data-reach-tooltip] {
        animation: ${fade} 0.3s ease;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    [data-reach-menu-list][data-reach-menu-list] {
        box-shadow: rgba(0, 0, 0, 0.24) 0 16px 24px;
        background: ${props => props.theme.root.colors.background['02Second']};
        border-radius: 8px;
        padding: 8px 0;
        margin-top: 10px;
        border: 0 none;
    }

    [data-reach-menu-item][data-reach-menu-item][data-selected] {
        background: ${props => props.theme.root.colors.basicColors['4%']};
        color: inherit;
    }

    [data-reach-menu-item][data-reach-menu-item] {
        padding: 8px 16px;
    }

    [data-reach-tooltip] {
        border-radius: 8px;
        z-index: 9999;
        pointer-events: none;
        position: absolute;
        padding: 6px 8px;
        max-width: 138px;
        background: ${props => props.theme.root.colors.basicColors['90%']};
        color: ${props => props.theme.root.colors.basicColors.inverted['100%']};
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
    }

    .ql-editor {
        margin: 0;
        padding: 12px 0 12px 8px;
        overflow: hidden;
        overflow-wrap: anywhere;

        a {
            color: ${props => props.theme.root.colors.accent.themeAccent};
            text-decoration: underline;
        }
    }

    .ql-editor.ql-editor.ql-blank::before {
        color: ${props => props.theme.root.colors.basicColors['40%']};
        font-style: normal;
    }

    .ql-container {
        font-size: ${props => props.textMessageSize}px;
        line-height: ${props => FONT_SIZE_LINE_HEIGHTS[String(props.textMessageSize)]};
        font-family:  ${props => props.theme.fontFamily};
    }

    .ql-container.ql-snow {
        position: unset;
    }

    .ql-container .ql-editor .ql-font-monospace {
        font-family: Roboto Mono, ${props => props.theme.fontFamily};
    }

    .ql-mention-list-container {
        background: ${props => props.theme.root.colors.background['02Second']};
        color: inherit;
    }

    .ql-mention-list-item.selected {
        background: ${props => props.theme.root.colors.basicColors['4%']};
    }

    @-moz-document url-prefix() {
        * {
            scrollbar-width: thin;
        }

        * {
            scrollbar-color: ${props => props.theme.root.colors.basicColors['10%']} transparent;
        }
    }

    *::-webkit-scrollbar {
        width: 6px;
        height: 8px;
    }

    *::-webkit-scrollbar-thumb {
        background-color: ${props => props.theme.root.colors.basicColors['10%']};
        border-radius: 10px;
    }

  *::-webkit-scrollbar-track {
      background-color: transparent;
  }

  *::-webkit-scrollbar-track-piece:start {
      background: transparent;
  }

  *::-webkit-scrollbar-track-piece:end {
      background: transparent;
  }
  
  button {
      padding: 0;
      border: 0;
      outline: 0;
      vertical-align: baseline;
      background-color: transparent;
  }
`;

export default Global;
